import React, { useEffect } from "react";
import { Modal, Form, Cascader, Typography, Button } from "antd";
import { orgUnitFromCascadeValue, orgUnitCascaderValue, PracticeArea } from "~/design-system";
import { VPDLabel } from "~/context/taxonomy-context";

interface PracticeAreaDefaultModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (practiceAreaIds: number[]) => void;
  defaultForOrgUnitIds: number[];
  practiceAreas: PracticeArea[];
}

export const PracticeAreaDefaultModal = ({
  visible,
  onCancel,
  onSubmit,
  defaultForOrgUnitIds,
  practiceAreas,
}: PracticeAreaDefaultModalProps) => {
  const [form] = Form.useForm();

  const PA_LABEL = VPDLabel("PracticeArea");

  const paOptions = practiceAreas
    ?.filter((x) => !x.parent?.id)
    .map((pa) => {
      return {
        value: pa.id,
        label: pa.name,
        children:
          practiceAreas
            .filter((x) => x.parent?.id === pa.id)
            .map((y) => ({ value: y.id, label: y.name })) || [],
      };
    });

  useEffect(() => {
    if (visible) {
      if (defaultForOrgUnitIds && defaultForOrgUnitIds.length > 0) {
        const cascaderValues = defaultForOrgUnitIds
          .map((id) => orgUnitCascaderValue(practiceAreas, id))
          .filter(Boolean);

        form.setFieldsValue({
          practiceAreaIds: cascaderValues,
        });
      } else {
        form.setFieldsValue({
          practiceAreaIds: [],
        });
      }
    }
  }, [visible, defaultForOrgUnitIds, form, practiceAreas]);

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const selectedPracticeAreaIds = values.practiceAreaIds?.map(orgUnitFromCascadeValue) || [];
      onSubmit(selectedPracticeAreaIds);
    });
  };

  const handleClearDefaults = () => {
    form.setFieldsValue({ practiceAreaIds: [] });
    onSubmit([]);
  };

  return (
    <Modal
      width={500}
      open={visible}
      title={`Set Default For ${PA_LABEL}`}
      onCancel={onCancel}
      onOk={handleSubmit}
      footer={[
        <Button key="clear" onClick={handleClearDefaults}>
          Clear Defaults
        </Button>,
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
    >
      <Typography.Text style={{ marginBottom: "16px", display: "block" }}>
        Selected practices areas will use this design by default, overriding the global default.
      </Typography.Text>
      <Form form={form}>
        <Form.Item label={PA_LABEL + "s"} name="practiceAreaIds">
          <Cascader
            showSearch
            allowClear
            placeholder={`Select ${PA_LABEL}s`}
            options={paOptions}
            multiple
            showCheckedStrategy={Cascader.SHOW_CHILD}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
